/**
 * ThreadManager — Threads & Messaging function group, MVP slice.
 *
 * This module wires the `createThread` function end-to-end through the
 * existing infrastructure. It exists to prove the function map maps to
 * real code, not just diagrams.
 *
 * What createThread() touches, in order:
 *
 *   1. Identity        — current context (per-context sub-key in hierarchical mode)
 *   2. GroupEncryption — generate MLS key package for self, create MLS group
 *   3. SyncProvider    — open the OrbitDB stream for the new thread
 *   4. Envelope        — sign a thread-creation envelope and publish it
 *   5. MeshDatabase    — insert ThreadIndexRecord + MlsStateRecord
 *   6. AuditLog        — write a 'thread.created' entry
 *
 * Failure modes are explicit at each step. If MLS group creation fails,
 * we never touch storage. If storage insertion fails after MLS succeeds,
 * we attempt rollback (caveat: rollback in IDB is best-effort).
 *
 * Future functions in the Threads group (sendMessage, inviteToThread,
 * leaveThread, etc.) follow this same pattern: open stream → modify MLS
 * state → sign envelope → append to stream → update local cache → audit.
 * The cache hydration loop (subscribing to received envelopes and
 * populating message_cache) is handled in a separate ThreadCacheSync
 * module not in scope for this slice.
 */

import Dexie from 'dexie';
import { v4 as uuid } from 'uuid';

import type { AppContext } from '../app';
import { signEnvelope } from '../envelope/Envelope';
import type { GroupId, KeyPackage } from '../envelope/GroupEncryption';
import type { Context } from '../identity';
import type {
  AuditLogRecord,
  MlsStateRecord,
  MuteRecord,
  ReadStateRecord,
  ThreadIndexRecord,
  ThreadKind,
} from '../storage/schema';

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

export interface CreateThreadOptions {
  /** Display name for the thread. */
  name: string;

  /** What kind of thread: chat, data-subject, etc. Defaults to 'chat'. */
  kind?: ThreadKind;

  /** If kind === 'data-subject', the dataset row this thread annotates. */
  datasetRowId?: string;

  /**
   * Context to create the thread in. In global mode there's only one
   * context so this can be omitted. In hierarchical mode the caller
   * specifies the per-context identity to use.
   */
  context?: Context;
}

export interface CreateThreadResult {
  threadId: string;
  groupId: GroupId;
  streamId: string;
  /** The signed thread-creation envelope, for callers who want to inspect or share it. */
  creationEnvelopeHash: string;
}

export class ThreadManager {
  constructor(private ctx: AppContext) {}

  async createThread(opts: CreateThreadOptions): Promise<CreateThreadResult> {
    const ctx = this.ctx;
    const context = opts.context ?? defaultContext();
    const kind: ThreadKind = opts.kind ?? 'chat';

    // ── Step 1: generate identifiers ────────────────────────────────────
    const threadId = uuid();
    const groupId: GroupId = { value: threadId };
    const streamId = `mesh-thread-${threadId}`;

    // ── Step 2: MLS group creation ──────────────────────────────────────
    // Generate a KeyPackage for ourselves (label = our pubkey hex for now;
    // future work will use a proper credential identity).
    let creatorKeyPackage: KeyPackage;
    let creatorPrivate: Uint8Array;
    try {
      const myPubkey = await ctx.identity.pubkey(context);
      const myLabel = bytesToHex(myPubkey);
      const kp = await ctx.groupEnc.generateKeyPackage(myLabel);
      creatorKeyPackage = kp.keyPackage;
      creatorPrivate = kp.privateBytes;
    } catch (err) {
      throw new Error(`ThreadManager.createThread: failed to generate key package: ${errMsg(err)}`);
    }

    let mlsState: import('../envelope/GroupEncryption').GroupState;
    try {
      mlsState = await ctx.groupEnc.createGroup({
        groupId,
        creatorLabel: bytesToHex(await ctx.identity.pubkey(context)),
        creatorKeyPackage,
        creatorPrivate,
      });
    } catch (err) {
      throw new Error(`ThreadManager.createThread: MLS group creation failed: ${errMsg(err)}`);
    }

    // ── Step 3: open the OrbitDB stream ─────────────────────────────────
    // If this fails, MLS state stays in memory but we haven't persisted
    // anything yet. Caller can retry safely.
    try {
      await ctx.sync.open(streamId, context);
    } catch (err) {
      throw new Error(`ThreadManager.createThread: failed to open sync stream: ${errMsg(err)}`);
    }

    // ── Step 4: sign + append the thread-creation envelope ──────────────
    // This is the network-visible record of the thread coming into being.
    // Its payload is small JSON, plaintext at the application layer
    // (the *membership* and *messages* are MLS-protected; the existence
    // of the thread itself is intentionally observable at the OrbitDB
    // layer, since that's how peers learn to subscribe).
    const creationPayload = JSON.stringify({
      kind: 'thread-created',
      threadId,
      name: opts.name,
      threadKind: kind,
      ...(opts.datasetRowId !== undefined && { datasetRowId: opts.datasetRowId }),
      createdAt: new Date().toISOString(),
    });

    let envelopeHash: string;
    try {
      const env = await signEnvelope({
        identity: ctx.identity,
        context,
        contentType: 'application/json; profile=thread-created',
        payload: creationPayload,
        lamport: 0,
        parentHash: '',
      });
      await ctx.sync.append(streamId, env);
      envelopeHash = env.hash;
    } catch (err) {
      // Stream is open but no entries — leaving it open is fine; if the
      // caller retries with the same threadId, we'd hit a duplicate-stream
      // case (handled by SyncProvider).
      throw new Error(`ThreadManager.createThread: failed to append creation envelope: ${errMsg(err)}`);
    }

    // ── Step 5: local index + MLS state persistence ─────────────────────
    const now = new Date().toISOString();
    const contextKey = contextToKey(context);
    const db = ctx.storage.db;

    const threadIndex: ThreadIndexRecord = {
      threadId,
      context: contextKey,
      name: opts.name,
      kind,
      ...(opts.datasetRowId !== undefined && { datasetRowId: opts.datasetRowId }),
      lastActivityAt: now,
      unreadCount: 0,
      muteUntil: null,
      memberCount: mlsState.memberCount,
      archived: false,
      createdAt: now,
    };

    // Encrypt the MLS state blob under the session key. We use the
    // StorageRouter's session key (already in memory after unlock).
    const mlsStateRecord: MlsStateRecord = {
      threadId,
      context: contextKey,
      epoch: mlsState.epoch,
      memberCount: mlsState.memberCount,
      cipherSuite: cipherSuiteName(mlsState),
      ciphertext: await ctx.storage.encryptForSession(mlsState.opaque),
      updatedAt: now,
    };

    try {
      // Single transaction: either both inserts succeed or neither does.
      await db.transaction('rw', [db.threads_index, db.mls_state], async () => {
        await db.threads_index.add(threadIndex);
        await db.mls_state.add(mlsStateRecord);
      });
    } catch (err) {
      // Storage failure is recoverable in principle (the envelope is on
      // the OrbitDB log; on next boot we could rehydrate from there),
      // but for alpha we just report it loudly.
      throw new Error(`ThreadManager.createThread: storage write failed: ${errMsg(err)}`);
    }

    // ── Step 6: audit log ───────────────────────────────────────────────
    // Audit is best-effort: a failure here doesn't roll back the thread,
    // since the thread is otherwise a valid persisted artifact. We log
    // the audit failure to the console.
    try {
      await this.writeAudit({
        category: 'thread',
        action: 'thread.created',
        context: contextKey,
        details: {
          threadId,
          kind,
          name: opts.name,
          envelopeHash,
          memberCount: mlsState.memberCount,
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`ThreadManager.createThread: audit write failed: ${errMsg(err)}`);
    }

    return {
      threadId,
      groupId,
      streamId,
      creationEnvelopeHash: envelopeHash,
    };
  }

  // ───────────────────────────────────────────────────────────────────────
  // sendMessage
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Send a message to an existing thread.
   *
   * Steps:
   *   1. Load thread index + MLS state from local storage
   *   2. Encrypt the plaintext via the thread's MLS group (advances state)
   *   3. Sign an envelope wrapping the ciphertext, append to sync stream
   *   4. Persist the advanced MLS state + bump lastActivityAt
   *   5. Audit
   *
   * Each thread's send/receive operations must be serialized — MLS state
   * is not safe under concurrent advance. ThreadManager achieves this with
   * a per-thread mutex (`#threadMutexes` map). Callers may invoke
   * sendMessage from multiple call sites without coordinating; the
   * manager queues them.
   */
  async sendMessage(opts: SendMessageOptions): Promise<SendMessageResult> {
    const { threadId, text } = opts;
    return this.withThreadLock(threadId, async () => {
      const ctx = this.ctx;
      const db = ctx.storage.db;

      // ── Step 1: load thread index + MLS state ───────────────────────
      const threadIndex = await db.threads_index.get(threadId);
      if (!threadIndex) {
        throw new Error(`ThreadManager.sendMessage: unknown thread ${threadId}`);
      }
      const mlsState = await this.loadGroupState(threadId);

      // The context for signing the envelope is derived from the thread's
      // context column. We currently use a stable purpose-string per
      // thread; future work may scope differently.
      const context: Context = {
        environment: 'chat',
        purpose: `thread-${threadId}`,
      };

      // ── Step 2: encrypt plaintext via MLS ──────────────────────────
      const plaintextBytes = new TextEncoder().encode(text);
      const encryptResult = await ctx.groupEnc.encrypt(mlsState, plaintextBytes);

      // ── Step 3: sign + append envelope ─────────────────────────────
      // The ciphertext is base64-encoded into the envelope's text payload.
      // The signed envelope is what lands on the OrbitDB stream; the
      // ciphertext is the inner contents.
      const ciphertextB64 = bytesToBase64(encryptResult.ciphertext);
      const envelopePayload = JSON.stringify({
        kind: 'message',
        ciphertext: ciphertextB64,
      });

      const env = await signEnvelope({
        identity: ctx.identity,
        context,
        contentType: 'application/json; profile=thread-message',
        payload: envelopePayload,
        lamport: 0,  // TODO(beta): wire a real Lamport clock per thread
        parentHash: '',
      });

      await ctx.sync.append(`mesh-thread-${threadId}`, env);

      // ── Step 4: persist new MLS state + bump lastActivityAt ────────
      const now = new Date().toISOString();
      const newMlsRecord: MlsStateRecord = {
        threadId,
        context: threadIndex.context,
        epoch: encryptResult.newState.epoch,
        memberCount: encryptResult.newState.memberCount,
        cipherSuite: cipherSuiteName(encryptResult.newState),
        ciphertext: await ctx.storage.encryptForSession(encryptResult.newState.opaque),
        updatedAt: now,
      };

      await db.transaction('rw', [db.mls_state, db.threads_index], async () => {
        await db.mls_state.put(newMlsRecord);
        await db.threads_index.update(threadId, { lastActivityAt: now });
      });

      // ── Step 5: audit ──────────────────────────────────────────────
      try {
        await this.writeAudit({
          category: 'thread',
          action: 'thread.message-sent',
          context: threadIndex.context,
          details: {
            threadId,
            envelopeHash: env.hash,
            ciphertextLength: encryptResult.ciphertext.length,
            epoch: encryptResult.newState.epoch,
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`ThreadManager.sendMessage: audit write failed: ${errMsg(err)}`);
      }

      return {
        envelopeHash: env.hash,
        epoch: encryptResult.newState.epoch,
      };
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // listThreads
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Return all threads the local user is a member of, sorted by most
   * recent activity. Pure read against the local index — never hits the
   * network. Returns whatever is cached.
   */
  async listThreads(opts: ListThreadsOptions = {}): Promise<ThreadIndexRecord[]> {
    const db = this.ctx.storage.db;
    let query = db.threads_index.orderBy('lastActivityAt').reverse();
    if (opts.includeArchived !== true) {
      // Dexie doesn't compose orderBy+where(boolean) cleanly; filter in JS.
      const all = await query.toArray();
      return all.filter((t) => t.archived === false);
    }
    return query.toArray();
  }

  // ───────────────────────────────────────────────────────────────────────
  // getMessages
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Read decrypted messages for a thread from the local cache, in
   * chronological order. The cache is populated by ThreadInbox; on a
   * freshly-loaded device with no inbox running, this returns whatever
   * was previously cached (possibly nothing). The caller is responsible
   * for starting the inbox if they want live updates.
   */
  async getMessages(opts: GetMessagesOptions): Promise<MessageCacheRow[]> {
    const { threadId, limit = 100 } = opts;
    const db = this.ctx.storage.db;
    const ctx = this.ctx;

    const rows = await db.message_cache
      .where('[context+threadId]')
      .between(
        [Dexie.minKey, threadId],
        [Dexie.maxKey, threadId],
      )
      .reverse()
      .limit(limit)
      .toArray();

    // Decrypt each row's ciphertext (encrypted under session key) and
    // shape into a friendlier return type. Reverse to chronological order.
    const out: MessageCacheRow[] = [];
    for (const r of rows.reverse()) {
      const plaintextBytes = await ctx.storage.decryptForSession(r.ciphertext);
      const text = new TextDecoder().decode(plaintextBytes);
      out.push({
        hash: r.hash,
        threadId: r.threadId,
        senderPubkey: r.senderPubkey,
        sentAt: r.sentAt,
        receivedAt: r.receivedAt,
        kind: r.kind,
        text,
      });
    }
    return out;
  }

  // ───────────────────────────────────────────────────────────────────────
  // markRead
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Mark a thread read up to a specific message. Idempotent — calling
   * with an older messageHash than the current pointer is a no-op (we
   * don't move the pointer backward). Calling with a newer one advances it.
   *
   * If `messageHash` is omitted, marks the entire thread read by moving
   * the pointer to the most recently cached message. Also zeroes
   * `unreadCount` on the threads_index row.
   *
   * Local-only operation — read state never syncs.
   */
  async markRead(opts: MarkReadOptions): Promise<void> {
    const { threadId } = opts;
    const ctx = this.ctx;
    const db = ctx.storage.db;

    const threadIndex = await db.threads_index.get(threadId);
    if (!threadIndex) {
      throw new Error(`ThreadManager.markRead: unknown thread ${threadId}`);
    }

    // Resolve target messageHash. If unspecified, use the latest cached
    // message hash for this thread (most recent sentAt).
    let targetHash = opts.messageHash;
    let targetSentAt: string | undefined;

    if (targetHash === undefined) {
      const allInThread = await db.message_cache
        .where('[context+threadId]')
        .equals([threadIndex.context, threadId])
        .sortBy('sentAt');
      const top = allInThread[allInThread.length - 1];  // most recent
      if (top === undefined) {
        // No cached messages; just zero the unread count and return.
        await db.threads_index.update(threadId, { unreadCount: 0 });
        return;
      }
      targetHash = top.hash;
      targetSentAt = top.sentAt;
    } else {
      const cached = await db.message_cache.get(targetHash);
      if (cached === undefined) {
        throw new Error(
          `ThreadManager.markRead: messageHash ${targetHash} not in local cache`,
        );
      }
      targetSentAt = cached.sentAt;
    }

    const recordId = `${threadIndex.context}:${threadId}`;
    const existing = await db.read_state.get(recordId);
    const now = new Date().toISOString();

    // Don't move pointer backward.
    if (existing !== undefined && existing.lastReadMessageAt > targetSentAt!) {
      return;
    }

    const record: ReadStateRecord = {
      id: recordId,
      context: threadIndex.context,
      threadId,
      lastReadMessageHash: targetHash,
      lastReadMessageAt: targetSentAt!,
      markedAt: now,
    };

    await db.transaction('rw', [db.read_state, db.threads_index], async () => {
      await db.read_state.put(record);
      await db.threads_index.update(threadId, { unreadCount: 0 });
    });

    // Read events are high-volume and low-value for audit; skip the audit
    // entry here. Future work might log a summary periodically.
  }

  // ───────────────────────────────────────────────────────────────────────
  // muteThread / unmuteThread
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Mute a thread until a specific time, or indefinitely. The mute affects
   * UI hints only — incoming messages still arrive and cache normally;
   * the threads_index row gets `muteUntil` set, and a row in mute_table
   * records the operation for audit.
   */
  async muteThread(opts: MuteThreadOptions): Promise<void> {
    const { threadId } = opts;
    const ctx = this.ctx;
    const db = ctx.storage.db;

    const threadIndex = await db.threads_index.get(threadId);
    if (!threadIndex) {
      throw new Error(`ThreadManager.muteThread: unknown thread ${threadId}`);
    }

    // Resolve muteUntil: explicit timestamp, or "forever" if undefined.
    // We represent "forever" as the ISO of a date far in the future.
    const muteUntil = opts.until ?? '9999-12-31T23:59:59.000Z';
    const now = new Date().toISOString();
    const recordId = `${threadIndex.context}:${threadId}`;

    const record: MuteRecord = {
      id: recordId,
      context: threadIndex.context,
      threadId,
      muteUntil,
      setAt: now,
    };

    await db.transaction('rw', [db.mute_table, db.threads_index], async () => {
      await db.mute_table.put(record);
      await db.threads_index.update(threadId, { muteUntil });
    });

    try {
      await this.writeAudit({
        category: 'thread',
        action: 'thread.muted',
        context: threadIndex.context,
        details: { threadId, muteUntil },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`ThreadManager.muteThread: audit failed: ${errMsg(err)}`);
    }
  }

  /**
   * Clear the mute on a thread. Symmetric inverse of muteThread.
   */
  async unmuteThread(threadId: string): Promise<void> {
    const ctx = this.ctx;
    const db = ctx.storage.db;

    const threadIndex = await db.threads_index.get(threadId);
    if (!threadIndex) {
      throw new Error(`ThreadManager.unmuteThread: unknown thread ${threadId}`);
    }
    const recordId = `${threadIndex.context}:${threadId}`;

    await db.transaction('rw', [db.mute_table, db.threads_index], async () => {
      await db.mute_table.delete(recordId);
      await db.threads_index.update(threadId, { muteUntil: null });
    });

    try {
      await this.writeAudit({
        category: 'thread',
        action: 'thread.unmuted',
        context: threadIndex.context,
        details: { threadId },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`ThreadManager.unmuteThread: audit failed: ${errMsg(err)}`);
    }
  }

  /**
   * Returns true if the thread is currently muted (muteUntil in the future
   * AND a mute_table row exists). Convenience for UI.
   */
  async isMuted(threadId: string): Promise<boolean> {
    const ctx = this.ctx;
    const idx = await ctx.storage.db.threads_index.get(threadId);
    if (idx === undefined || idx.muteUntil === null) return false;
    return new Date(idx.muteUntil).getTime() > Date.now();
  }

  // ───────────────────────────────────────────────────────────────────────
  // generateKeyPackageForSharing
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Generate a fresh KeyPackage for ourselves and persist its private
   * half locally. Returns the public part as a base64-encoded bundle the
   * user can share out of band (QR, link, copy-paste) with others who
   * want to invite us to threads.
   *
   * KeyPackages are single-use; each call produces a new one. Generate
   * one whenever you anticipate being invited — keeping a small pool
   * (say, 5 unspent) is reasonable; refresh periodically.
   *
   * The returned `shareBundle` is a JSON envelope containing the public
   * KeyPackage bytes plus a label identifying the user. The bundle is
   * not signed by anything outside the KeyPackage itself — recipients
   * trust it because they trust the channel they got it through.
   */
  async generateKeyPackageForSharing(opts: { context?: Context } = {}): Promise<{
    kpHash: string;
    shareBundle: string;
  }> {
    const ctx = this.ctx;
    const context = opts.context ?? defaultContext();

    const myPubkey = await ctx.identity.pubkey(context);
    const myLabel = bytesToHex(myPubkey);

    const kp = await ctx.groupEnc.generateKeyPackage(myLabel);
    const kpHash = await hashBytes(kp.keyPackage.bytes);

    // Persist private bytes encrypted under session key.
    const privateBytesCipher = await ctx.storage.encryptForSession(kp.privateBytes);
    const record: import('../storage/schema').OwnKeyPackageRecord = {
      kpHash,
      context: contextToKey(context),
      cipherSuite: kp.keyPackage.impl === 'plaintext' ? 'plaintext' : 'mls',
      publicBytes: kp.keyPackage.bytes,
      privateBytesCipher,
      createdAt: new Date().toISOString(),
      usedAt: null,
    };
    await ctx.storage.db.own_key_packages.put(record);

    // Build a portable bundle for sharing.
    const shareBundle = JSON.stringify({
      kind: 'mesh-keypackage-share',
      v: 1,
      label: myLabel,
      impl: kp.keyPackage.impl,
      publicBytes: bytesToBase64(kp.keyPackage.bytes),
    });

    try {
      await this.writeAudit({
        category: 'thread',
        action: 'thread.keypackage-generated',
        context: contextToKey(context),
        details: { kpHash, cipherSuite: record.cipherSuite },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`ThreadManager.generateKeyPackageForSharing: audit failed: ${errMsg(err)}`);
    }

    return { kpHash, shareBundle };
  }

  // ───────────────────────────────────────────────────────────────────────
  // inviteToThread
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Add a member to an existing thread.
   *
   * Steps:
   *   1. Take per-thread lock; load existing MLS state.
   *   2. Call groupEnc.addMember(state, theirKeyPackage) → CommitResult.
   *   3. Persist advanced MLS state.
   *   4. Sign + publish a commit envelope to the thread stream so other
   *      existing members can update their state.
   *   5. Update threads_index.memberCount.
   *   6. Mark the contact's KeyPackage as used (if invitation came from
   *      a contact record) so we don't reuse it.
   *   7. Audit.
   *
   * Returns the WelcomeMessage bytes. The caller is responsible for
   * delivering this to the invitee out of band — Surface Mesh does not
   * yet have a per-user inbox channel for welcome delivery (a beta
   * feature: `mesh-inbox-{userPubkey}`).
   *
   * For MVP, the typical out-of-band delivery is to hand-copy the welcome
   * bundle to the invitee via QR or paste; in tests we deliver it directly
   * to the receiver's `acceptInvite`.
   */
  async inviteToThread(opts: InviteToThreadOptions): Promise<InviteToThreadResult> {
    const { threadId } = opts;
    return this.withThreadLock(threadId, async () => {
      const ctx = this.ctx;
      const db = ctx.storage.db;

      // Resolve the invitee's KeyPackage. Either passed directly via
      // `keyPackageBundle` (a share-bundle string from
      // generateKeyPackageForSharing) or looked up via contactId.
      let publicBytes: Uint8Array;
      let inviteeLabel: string;
      let contactId: string | undefined;

      if (opts.keyPackageBundle !== undefined) {
        const parsed = parseShareBundle(opts.keyPackageBundle);
        publicBytes = parsed.publicBytes;
        inviteeLabel = parsed.label;
      } else if (opts.contactId !== undefined) {
        const contact = await db.contacts.get(opts.contactId);
        if (!contact) {
          throw new Error(`ThreadManager.inviteToThread: unknown contact ${opts.contactId}`);
        }
        if (contact.keyPackageBytes === undefined) {
          throw new Error(
            `ThreadManager.inviteToThread: contact ${opts.contactId} has no KeyPackage; ` +
              'exchange one first via generateKeyPackageForSharing.',
          );
        }
        if (contact.keyPackageUsedAt !== undefined) {
          throw new Error(
            `ThreadManager.inviteToThread: contact ${opts.contactId}'s KeyPackage was already consumed at ${contact.keyPackageUsedAt}; ` +
              'request a fresh one before re-inviting.',
          );
        }
        publicBytes = contact.keyPackageBytes;
        // Use the contact's pubkey (not alias) as the memberLabel — the
        // acceptInvite path looks up the unspent KeyPackage by matching
        // this label against the recipient's pubkey-hex. The alias is
        // display metadata only.
        inviteeLabel = contact.pubkey;
        contactId = opts.contactId;
      } else {
        throw new Error(
          'ThreadManager.inviteToThread: must provide either keyPackageBundle or contactId',
        );
      }

      // Load thread index + MLS state.
      const threadIndex = await db.threads_index.get(threadId);
      if (!threadIndex) {
        throw new Error(`ThreadManager.inviteToThread: unknown thread ${threadId}`);
      }
      const state = await this.loadGroupState(threadId);

      // Reconstruct the KeyPackage in the shape addMember expects.
      const keyPackage: KeyPackage = {
        impl: state.impl,
        memberLabel: inviteeLabel,
        bytes: publicBytes,
      };

      const commit = await ctx.groupEnc.addMember(state, keyPackage);
      if (commit.welcome === undefined) {
        throw new Error('ThreadManager.inviteToThread: addMember did not produce a welcome message');
      }

      // Persist new state and publish commit envelope.
      const now = new Date().toISOString();
      const context: Context = {
        environment: 'chat',
        purpose: `thread-${threadId}`,
      };

      // Commit envelope — other existing members process this to advance
      // their own MLS state. For MVP single-member scenarios (Alice alone
      // before invite), no one else needs it; we publish anyway for protocol
      // correctness.
      const commitEnvelope = await signEnvelope({
        identity: ctx.identity,
        context,
        contentType: 'application/json; profile=thread-mls-commit',
        payload: JSON.stringify({
          kind: 'mls-control',
          mlsKind: 'commit',
          // Commits over plaintext impl have no separately-publishable bytes;
          // for MLS impl we'd encode the commit message here. The CommitResult
          // doesn't expose the commit bytes through the current interface;
          // mark as TODO(beta).
          bytes: '',  // TODO(beta): wire actual MLS commit bytes through CommitResult
        }),
        lamport: 0,
        parentHash: '',
      });

      await ctx.sync.append(`mesh-thread-${threadId}`, commitEnvelope);

      const newMlsRecord: MlsStateRecord = {
        threadId,
        context: threadIndex.context,
        epoch: commit.newState.epoch,
        memberCount: commit.newState.memberCount,
        cipherSuite: cipherSuiteName(commit.newState),
        ciphertext: await ctx.storage.encryptForSession(commit.newState.opaque),
        updatedAt: now,
      };

      await db.transaction('rw', [db.mls_state, db.threads_index, db.contacts], async () => {
        await db.mls_state.put(newMlsRecord);
        await db.threads_index.update(threadId, {
          memberCount: commit.newState.memberCount,
          lastActivityAt: now,
        });
        if (contactId !== undefined) {
          await db.contacts.update(contactId, {
            keyPackageUsedAt: now,
          });
        }
      });

      // Bundle the welcome for delivery to the invitee. We serialize
      // both the WelcomeMessage bytes and the metadata they need to
      // construct their local threads_index row.
      const welcomeBundle = JSON.stringify({
        kind: 'mesh-welcome-bundle',
        v: 1,
        threadId,
        groupId: threadId,
        threadName: threadIndex.name,
        threadKind: threadIndex.kind,
        senderLabel: bytesToHex(await ctx.identity.pubkey(context)),
        impl: commit.welcome.impl,
        forMember: commit.welcome.forMember,
        welcomeBytes: bytesToBase64(commit.welcome.bytes),
      });

      try {
        await this.writeAudit({
          category: 'thread',
          action: 'thread.invited',
          context: threadIndex.context,
          details: {
            threadId,
            inviteeLabel,
            commitEpoch: commit.newState.epoch,
            commitEnvelopeHash: commitEnvelope.hash,
            ...(contactId !== undefined && { contactId }),
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`ThreadManager.inviteToThread: audit failed: ${errMsg(err)}`);
      }

      return {
        welcomeBundle,
        commitEnvelopeHash: commitEnvelope.hash,
        newEpoch: commit.newState.epoch,
      };
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // acceptInvite
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Accept a thread invite by processing a welcome bundle produced by
   * `inviteToThread`.
   *
   * Steps:
   *   1. Parse the welcome bundle; extract welcome bytes + thread metadata.
   *   2. Look up our matching `own_key_packages` row by KeyPackage hash
   *      (the welcome was addressed to a specific KeyPackage). The
   *      `forMember` label in the welcome is our own pubkey-hex — we use
   *      that to find the right row.
   *   3. Decrypt the private bytes; call groupEnc.joinGroup → new state.
   *   4. Persist mls_state row + threads_index row.
   *   5. Mark the consumed KeyPackage.
   *   6. Audit.
   *
   * Note: the caller is responsible for starting/attaching ThreadInbox to
   * the newly-joined thread afterward (see ThreadInbox.attachThread).
   */
  async acceptInvite(opts: AcceptInviteOptions): Promise<AcceptInviteResult> {
    const ctx = this.ctx;
    const db = ctx.storage.db;
    const parsed = parseWelcomeBundle(opts.welcomeBundle);

    return this.withThreadLock(parsed.threadId, async () => {
      // Find our matching KeyPackage. The welcome's `forMember` is the
      // memberLabel passed to generateKeyPackage — which we set to our
      // own pubkey hex. We look up by that.
      const expectedLabel = parsed.forMember;
      // Dexie's index lookup with null is unreliable across versions; fetch
      // all and filter in JS. own_key_packages is bounded (a user generates
      // a handful, not thousands) so this is fine.
      const allOwn = await db.own_key_packages.toArray();
      const candidates = allOwn.filter((c) => c.usedAt === null);

      // The expectedLabel should match the hex of our pubkey at some
      // context. We don't have a direct label-to-row mapping; instead
      // try each candidate's stored publicBytes label.
      // For MVP we just take the most recent unspent KeyPackage that
      // was generated for a context whose pubkey matches the expected label.
      let chosen: import('../storage/schema').OwnKeyPackageRecord | undefined;
      for (const cand of candidates) {
        // Derive the context from the candidate's stored context column,
        // then check whether OUR pubkey under that context matches the
        // welcome's forMember label.
        const candContext = keyToContext(cand.context);
        const candPubkey = bytesToHex(await ctx.identity.pubkey(candContext));
        if (candPubkey === expectedLabel) {
          chosen = cand;
          break;
        }
      }

      if (!chosen) {
        throw new Error(
          `ThreadManager.acceptInvite: no unspent KeyPackage found for label ${expectedLabel}. ` +
            'Generate one first via generateKeyPackageForSharing.',
        );
      }

      // Reconstruct the WelcomeMessage and KeyPackage shapes joinGroup needs.
      const welcome: import('../envelope/GroupEncryption').WelcomeMessage = {
        impl: parsed.impl,
        groupId: { value: parsed.groupId },
        forMember: parsed.forMember,
        bytes: parsed.welcomeBytes,
      };

      const myKeyPackage: KeyPackage = {
        impl: chosen.cipherSuite === 'plaintext' ? 'plaintext' : 'mls',
        memberLabel: expectedLabel,
        bytes: chosen.publicBytes,
      };
      const myPrivate = await ctx.storage.decryptForSession(chosen.privateBytesCipher);

      // Join the group.
      const joinedState = await ctx.groupEnc.joinGroup({
        welcome,
        myKeyPackage,
        myPrivate,
      });

      // Persist mls_state + threads_index row + mark KeyPackage as used.
      const now = new Date().toISOString();
      const contextKey = chosen.context;

      const mlsRecord: MlsStateRecord = {
        threadId: parsed.threadId,
        context: contextKey,
        epoch: joinedState.epoch,
        memberCount: joinedState.memberCount,
        cipherSuite: cipherSuiteName(joinedState),
        ciphertext: await ctx.storage.encryptForSession(joinedState.opaque),
        updatedAt: now,
      };

      const indexRecord: ThreadIndexRecord = {
        threadId: parsed.threadId,
        context: contextKey,
        name: parsed.threadName,
        kind: parsed.threadKind,
        lastActivityAt: now,
        unreadCount: 0,
        muteUntil: null,
        memberCount: joinedState.memberCount,
        archived: false,
        createdAt: now,
      };

      await db.transaction('rw', [db.mls_state, db.threads_index, db.own_key_packages], async () => {
        await db.mls_state.put(mlsRecord);
        await db.threads_index.put(indexRecord);
        await db.own_key_packages.update(chosen!.kpHash, {
          usedAt: now,
          usedForThreadId: parsed.threadId,
        });
      });

      // Open the sync stream so subscribers can attach.
      const context: Context = {
        environment: 'chat',
        purpose: `thread-${parsed.threadId}`,
      };
      try {
        await ctx.sync.open(`mesh-thread-${parsed.threadId}`, context);
      } catch (err) {
        // Already-open is fine; surface other errors.
        if (!/already/i.test(errMsg(err))) {
          throw err;
        }
      }

      try {
        await this.writeAudit({
          category: 'thread',
          action: 'thread.invite-accepted',
          context: contextKey,
          details: {
            threadId: parsed.threadId,
            inviterLabel: parsed.senderLabel,
            consumedKpHash: chosen.kpHash,
            joinedEpoch: joinedState.epoch,
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`ThreadManager.acceptInvite: audit failed: ${errMsg(err)}`);
      }

      return {
        threadId: parsed.threadId,
        streamId: `mesh-thread-${parsed.threadId}`,
        joinedEpoch: joinedState.epoch,
      };
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // removeFromThread
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Remove another member from a thread. Produces an MLS commit that
   * cycles the group epoch — once committed, the removed member can no
   * longer decrypt future messages (post-compromise security on the
   * remove direction).
   *
   * Steps:
   *   1. Take per-thread lock; load existing MLS state.
   *   2. Call groupEnc.removeMember(state, memberLabel) → CommitResult.
   *   3. Persist advanced MLS state.
   *   4. Publish the commit envelope so other still-in members can update.
   *   5. Update threads_index.memberCount.
   *   6. Audit.
   *
   * The member's `memberLabel` parameter must match the label used when
   * they joined (typically their pubkey hex). Use the contact's pubkey,
   * not their alias.
   *
   * Caveat: under MlsGroupEncryption, this throws because the real
   * removeMember requires leaf-index lookup against the ratchet tree
   * which is not yet implemented (TODO(beta) in MlsGroupEncryption.ts).
   * Under PlaintextGroupEncryption it works.
   */
  async removeFromThread(opts: RemoveFromThreadOptions): Promise<RemoveFromThreadResult> {
    const { threadId, memberLabel } = opts;
    return this.withThreadLock(threadId, async () => {
      const ctx = this.ctx;
      const db = ctx.storage.db;

      const threadIndex = await db.threads_index.get(threadId);
      if (!threadIndex) {
        throw new Error(`ThreadManager.removeFromThread: unknown thread ${threadId}`);
      }
      const state = await this.loadGroupState(threadId);

      // Compute our own label so we can refuse to "remove" ourselves
      // via this method (use leaveThread for that).
      const context: Context = {
        environment: 'chat',
        purpose: `thread-${threadId}`,
      };
      const ourLabel = bytesToHex(await ctx.identity.pubkey(context));
      if (memberLabel === ourLabel) {
        throw new Error(
          'ThreadManager.removeFromThread: cannot remove yourself; use leaveThread() instead',
        );
      }

      let commit: import('../envelope/GroupEncryption').CommitResult;
      try {
        commit = await ctx.groupEnc.removeMember(state, memberLabel);
      } catch (err) {
        // Bubble up — most likely cause is the MLS impl's TODO(beta) for
        // leaf-index lookup.
        throw new Error(
          `ThreadManager.removeFromThread: removeMember failed: ${errMsg(err)}`,
        );
      }

      // Publish the commit envelope.
      const commitEnvelope = await signEnvelope({
        identity: ctx.identity,
        context,
        contentType: 'application/json; profile=thread-mls-commit',
        payload: JSON.stringify({
          kind: 'mls-control',
          mlsKind: 'commit',
          bytes: '',  // TODO(beta): wire actual MLS commit bytes
          op: 'remove',
          removed: memberLabel,
        }),
        lamport: 0,
        parentHash: '',
      });
      await ctx.sync.append(`mesh-thread-${threadId}`, commitEnvelope);

      // Persist advanced state and update memberCount.
      const now = new Date().toISOString();
      const newMlsRecord: MlsStateRecord = {
        threadId,
        context: threadIndex.context,
        epoch: commit.newState.epoch,
        memberCount: commit.newState.memberCount,
        cipherSuite: cipherSuiteName(commit.newState),
        ciphertext: await ctx.storage.encryptForSession(commit.newState.opaque),
        updatedAt: now,
      };
      await db.transaction('rw', [db.mls_state, db.threads_index], async () => {
        await db.mls_state.put(newMlsRecord);
        await db.threads_index.update(threadId, {
          memberCount: commit.newState.memberCount,
          lastActivityAt: now,
        });
      });

      try {
        await this.writeAudit({
          category: 'thread',
          action: 'thread.member-removed',
          context: threadIndex.context,
          details: {
            threadId,
            removed: memberLabel,
            newEpoch: commit.newState.epoch,
            commitEnvelopeHash: commitEnvelope.hash,
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`ThreadManager.removeFromThread: audit failed: ${errMsg(err)}`);
      }

      return {
        commitEnvelopeHash: commitEnvelope.hash,
        newEpoch: commit.newState.epoch,
        newMemberCount: commit.newState.memberCount,
      };
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // leaveThread
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Voluntarily leave a thread. Removes ourselves from the MLS group,
   * publishes the commit, and tears down all local thread state.
   *
   * After leaveThread completes, the thread no longer appears in
   * listThreads(), and the local mls_state, message_cache, and read_state
   * rows are deleted. The audit log entry remains as the forensic record.
   *
   * Note: the published commit envelope tells other members we've left.
   * Until they process it, they may still see us as a member in their
   * own threads_index — eventual consistency.
   *
   * Caveat: same MLS removeMember limitation as removeFromThread.
   */
  async leaveThread(threadId: string): Promise<LeaveThreadResult> {
    return this.withThreadLock(threadId, async () => {
      const ctx = this.ctx;
      const db = ctx.storage.db;

      const threadIndex = await db.threads_index.get(threadId);
      if (!threadIndex) {
        throw new Error(`ThreadManager.leaveThread: unknown thread ${threadId}`);
      }
      const state = await this.loadGroupState(threadId);

      const context: Context = {
        environment: 'chat',
        purpose: `thread-${threadId}`,
      };
      const ourLabel = bytesToHex(await ctx.identity.pubkey(context));

      let commit: import('../envelope/GroupEncryption').CommitResult;
      let commitEnvelopeHash: string | undefined;

      try {
        commit = await ctx.groupEnc.removeMember(state, ourLabel);

        // Publish the self-remove commit so other members can update.
        const commitEnvelope = await signEnvelope({
          identity: ctx.identity,
          context,
          contentType: 'application/json; profile=thread-mls-commit',
          payload: JSON.stringify({
            kind: 'mls-control',
            mlsKind: 'commit',
            bytes: '',
            op: 'leave',
            removed: ourLabel,
          }),
          lamport: 0,
          parentHash: '',
        });
        await ctx.sync.append(`mesh-thread-${threadId}`, commitEnvelope);
        commitEnvelopeHash = commitEnvelope.hash;
      } catch (err) {
        // For MLS impl with the leaf-lookup TODO, this will fail. We still
        // tear down local state, because the user's intent is clear — they
        // don't want this thread on their device anymore. The trade-off:
        // other members won't see our leave commit, so they'll continue
        // to think we're a member until their state is otherwise refreshed.
        // eslint-disable-next-line no-console
        console.warn(
          `ThreadManager.leaveThread: commit publish failed (proceeding with local teardown): ${errMsg(err)}`,
        );
      }

      // Local teardown: drop all rows associated with this thread.
      // We do this in a single transaction for atomicity. message_cache
      // rows can be many; Dexie handles bulk deletes well.
      await db.transaction(
        'rw',
        [db.threads_index, db.mls_state, db.message_cache, db.read_state, db.mute_table],
        async () => {
          await db.threads_index.delete(threadId);
          await db.mls_state.delete(threadId);
          await db.message_cache
            .where('[context+threadId]')
            .equals([threadIndex.context, threadId])
            .delete();
          const recordId = `${threadIndex.context}:${threadId}`;
          await db.read_state.delete(recordId);
          await db.mute_table.delete(recordId);
        },
      );

      try {
        await this.writeAudit({
          category: 'thread',
          action: 'thread.left',
          context: threadIndex.context,
          details: {
            threadId,
            ...(commitEnvelopeHash !== undefined && { commitEnvelopeHash }),
            teardown: 'complete',
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`ThreadManager.leaveThread: audit failed: ${errMsg(err)}`);
      }

      return {
        ...(commitEnvelopeHash !== undefined && { commitEnvelopeHash }),
      };
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // INTERNAL — MLS STATE LOAD/PERSIST + MUTEX
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Load an MLS group state from local storage. Reconstructs the
   * GroupState shape that groupEnc methods expect.
   *
   * Throws if the thread's mls_state row is missing, which indicates
   * either an unjoined thread or storage corruption.
   */
  private async loadGroupState(threadId: string): Promise<import('../envelope/GroupEncryption').GroupState> {
    const db = this.ctx.storage.db;
    const row = await db.mls_state.get(threadId);
    if (!row) {
      throw new Error(`ThreadManager.loadGroupState: no MLS state for thread ${threadId}`);
    }
    const opaque = await this.ctx.storage.decryptForSession(row.ciphertext);
    const impl: 'plaintext' | 'mls' = row.cipherSuite === 'plaintext' ? 'plaintext' : 'mls';
    return {
      impl,
      groupId: { value: threadId },
      epoch: row.epoch,
      memberCount: row.memberCount,
      opaque,
    };
  }

  /**
   * Per-thread mutex. MLS state is not safe under concurrent advance —
   * if two callers race to encrypt+persist, one will see stale state and
   * we get a "wrong epoch" error on the next decrypt. Serialize.
   */
  #threadMutexes = new Map<string, Promise<unknown>>();

  private withThreadLock<T>(threadId: string, fn: () => Promise<T>): Promise<T> {
    const prior = this.#threadMutexes.get(threadId) ?? Promise.resolve();
    const next = prior.then(() => fn(), () => fn()) as Promise<T>;
    // Note: we store the chained promise. Old entries are GC'd when no
    // longer referenced.
    this.#threadMutexes.set(threadId, next.catch(() => undefined));
    return next;
  }

  // Exposed for ThreadInbox so it can take the same lock when applying
  // received messages. Not part of the public API.
  public _withThreadLock<T>(threadId: string, fn: () => Promise<T>): Promise<T> {
    return this.withThreadLock(threadId, fn);
  }

  // Exposed for ThreadInbox.
  public _loadGroupState(threadId: string): Promise<import('../envelope/GroupEncryption').GroupState> {
    return this.loadGroupState(threadId);
  }

  // ───────────────────────────────────────────────────────────────────────
  // INTERNAL — AUDIT
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Write a signed audit entry. The signature is over the canonical
   * field order to be tamper-evident. For alpha, we use the
   * envelope-signing identity at the current context; future work will
   * use a separate device-bound audit key.
   */
  private async writeAudit(input: {
    category: AuditLogRecord['category'];
    action: string;
    context: string | null;
    details: Record<string, unknown>;
  }): Promise<void> {
    const ctx = this.ctx;
    const occurredAt = new Date().toISOString();

    // Build the canonical signing input. Order matters; mirror it on verify.
    const canonical = JSON.stringify({
      category: input.category,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
    });
    // Sign with the global context — audit log is device-wide.
    const signature = await ctx.identity.sign(
      new TextEncoder().encode(canonical),
      defaultContext(),
    );

    const record: AuditLogRecord = {
      // seq is auto-assigned by Dexie's ++seq index
      seq: undefined as unknown as number,
      category: input.category,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
      signature,
    };

    await ctx.storage.db.audit_log.add(record);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ADDITIONAL PUBLIC TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface SendMessageOptions {
  threadId: string;
  text: string;
}

export interface SendMessageResult {
  envelopeHash: string;
  /** The epoch of the MLS state after encryption. */
  epoch: number;
}

export interface InviteToThreadOptions {
  threadId: string;
  /** Provide either a share-bundle string (from generateKeyPackageForSharing
   * on the invitee's device) or a contactId whose row carries the KeyPackage. */
  keyPackageBundle?: string;
  contactId?: string;
}

export interface InviteToThreadResult {
  /** Serialized welcome bundle. Deliver this to the invitee out of band;
   * they pass it to `acceptInvite`. */
  welcomeBundle: string;
  /** Hash of the commit envelope published to the thread stream. */
  commitEnvelopeHash: string;
  /** The MLS epoch after the commit. */
  newEpoch: number;
}

export interface AcceptInviteOptions {
  /** Welcome bundle string from `inviteToThread`. */
  welcomeBundle: string;
}

export interface AcceptInviteResult {
  threadId: string;
  streamId: string;
  joinedEpoch: number;
}

export interface RemoveFromThreadOptions {
  threadId: string;
  /** The MLS memberLabel of the member to remove. For invites that went
   * through ContactManager, this is the contact.pubkey (hex). */
  memberLabel: string;
}

export interface RemoveFromThreadResult {
  commitEnvelopeHash: string;
  newEpoch: number;
  newMemberCount: number;
}

export interface LeaveThreadResult {
  /** Hash of the published self-remove commit, if it succeeded.
   * On MLS-impl groups with the removeMember TODO, this may be absent
   * — the local teardown still happens, just without a network signal. */
  commitEnvelopeHash?: string;
}

export interface ListThreadsOptions {
  /** Include archived threads in the result. Default false. */
  includeArchived?: boolean;
}

export interface GetMessagesOptions {
  threadId: string;
  /** Max number of most-recent messages to return. Default 100. */
  limit?: number;
}

export interface MarkReadOptions {
  threadId: string;
  /** If specified, mark this exact message as the new read pointer.
   * If omitted, mark the entire thread read (move pointer to the most
   * recent cached message). */
  messageHash?: string;
}

export interface MuteThreadOptions {
  threadId: string;
  /** ISO 8601 timestamp at which the mute expires. If omitted, the thread
   * is muted indefinitely (until explicitly unmuted). */
  until?: string;
}

/** Returned shape for getMessages. The on-disk record (MessageCacheRecord)
 * is encrypted; this is the decrypted, application-facing form. */
export interface MessageCacheRow {
  hash: string;
  threadId: string;
  senderPubkey: string;
  sentAt: string;
  receivedAt: string;
  kind: 'text' | 'attachment' | 'reaction' | 'edit' | 'delete' | 'system' | 'pin' | 'manifest-link';
  text: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function defaultContext(): Context {
  // Global mode pins context to a stable sentinel under 'chat'.
  // Hierarchical mode overrides this via the explicit `context` option on
  // createThread (different per-context sub-keys are derived for each purpose).
  return {
    environment: 'chat',
    purpose: 'thread-default',
  };
}

function contextToKey(ctx: Context): string {
  // Stable serialization for use as a Dexie index value. In global mode
  // every context-key is '*'-equivalent; in hierarchical mode the
  // environment+purpose pair uniquely identifies the context.
  return `${ctx.environment}:${ctx.purpose}${ctx.epoch !== undefined ? `:e${ctx.epoch}` : ''}`;
}

function bytesToHex(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) {
    s += b[i]!.toString(16).padStart(2, '0');
  }
  return s;
}

function bytesToBase64(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) {
    s += String.fromCharCode(b[i]!);
  }
  return btoa(s);
}

function base64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/** Hash arbitrary bytes to a hex string. Uses SHA-256 via Web Crypto. */
async function hashBytes(b: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', b as BufferSource);
  return bytesToHex(new Uint8Array(buf));
}

/** Inverse of contextToKey — reconstruct a Context from the stable string form. */
function keyToContext(key: string): Context {
  // Format: "{environment}:{purpose}" or "{environment}:{purpose}:e{epoch}"
  const parts = key.split(':');
  const environment = parts[0] as Context['environment'];
  const purpose = parts[1] ?? 'thread-default';
  if (parts.length >= 3 && parts[2]!.startsWith('e')) {
    const epoch = Number(parts[2]!.slice(1));
    return { environment, purpose, epoch };
  }
  return { environment, purpose };
}

interface ParsedShareBundle {
  label: string;
  publicBytes: Uint8Array;
  impl: 'plaintext' | 'mls';
}

function parseShareBundle(bundle: string): ParsedShareBundle {
  let obj: unknown;
  try {
    obj = JSON.parse(bundle);
  } catch {
    throw new Error('parseShareBundle: malformed JSON');
  }
  if (
    typeof obj !== 'object' || obj === null ||
    (obj as { kind?: unknown }).kind !== 'mesh-keypackage-share'
  ) {
    throw new Error('parseShareBundle: not a mesh-keypackage-share bundle');
  }
  const o = obj as { label?: unknown; publicBytes?: unknown; impl?: unknown };
  if (typeof o.label !== 'string' || typeof o.publicBytes !== 'string') {
    throw new Error('parseShareBundle: missing label or publicBytes');
  }
  return {
    label: o.label,
    publicBytes: base64ToBytes(o.publicBytes),
    impl: o.impl === 'mls' ? 'mls' : 'plaintext',
  };
}

interface ParsedWelcomeBundle {
  threadId: string;
  groupId: string;
  threadName: string;
  threadKind: 'chat' | 'data-subject' | 'system' | 'support';
  senderLabel: string;
  forMember: string;
  impl: 'plaintext' | 'mls';
  welcomeBytes: Uint8Array;
}

function parseWelcomeBundle(bundle: string): ParsedWelcomeBundle {
  let obj: unknown;
  try {
    obj = JSON.parse(bundle);
  } catch {
    throw new Error('parseWelcomeBundle: malformed JSON');
  }
  if (
    typeof obj !== 'object' || obj === null ||
    (obj as { kind?: unknown }).kind !== 'mesh-welcome-bundle'
  ) {
    throw new Error('parseWelcomeBundle: not a mesh-welcome-bundle');
  }
  const o = obj as Record<string, unknown>;
  const required = ['threadId', 'groupId', 'threadName', 'threadKind', 'senderLabel', 'forMember', 'welcomeBytes'];
  for (const k of required) {
    if (typeof o[k] !== 'string') {
      throw new Error(`parseWelcomeBundle: missing or invalid field ${k}`);
    }
  }
  return {
    threadId: o.threadId as string,
    groupId: o.groupId as string,
    threadName: o.threadName as string,
    threadKind: o.threadKind as ParsedWelcomeBundle['threadKind'],
    senderLabel: o.senderLabel as string,
    forMember: o.forMember as string,
    impl: o.impl === 'mls' ? 'mls' : 'plaintext',
    welcomeBytes: base64ToBytes(o.welcomeBytes as string),
  };
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Read the cipher suite name from an opaque MLS state envelope.
 * For plaintext impl this is a sentinel. For MLS impl, the cipher suite
 * is stored on the runtime; we read it from the impl discriminator. */
function cipherSuiteName(state: import('../envelope/GroupEncryption').GroupState): string {
  if (state.impl === 'plaintext') return 'plaintext';
  // MLS impl: the suite name is encoded into the serialized state blob.
  // For the contract we just record 'mls'; the precise suite is in the
  // opaque ciphertext alongside the rest of the state.
  return 'mls';
}
